import type { LockReason, ValidationFinding } from "@/api/hooks";
import { useLibraryAssetsQuery } from "@/api/library";
import { isResolvablePath } from "@/lib/fieldPath";
import { bySeverity, countBySeverity } from "@/lib/validationCodes";

import { FindingItem } from "./FindingItem";
import { useFieldAnchorRegistry } from "./FieldAnchorContext";

/**
 * An ordered, announced list of findings, each joined to its field's lock state
 * and, where the path resolves, to the control it is about.
 *
 * Ordering is by severity so the blocking findings are read first. Ordering is
 * stable within a severity — the service's order is preserved — so a re-run
 * that changes nothing does not reshuffle the list under the reader.
 */
export function FindingList({
  findings,
  lockReasonByPath,
  /** Set false on surfaces that already announce the report, to avoid a double read. */
  announce = true,
  /** The Project the report was run for (#1084's Pathways settings link). */
  projectId,
}: {
  findings: readonly ValidationFinding[];
  lockReasonByPath?: ReadonlyMap<string, LockReason> | undefined;
  announce?: boolean;
  projectId?: string | undefined;
}) {
  const { paths, focus } = useFieldAnchorRegistry();
  // #1084: the catalog is read only to put a NAME beside an attributed
  // library item — the same shared cache entry every adoption surface reads,
  // so no second observer of a different key. Ownership and editability come
  // from the service on the finding itself; a catalog that has not loaded
  // leaves the item named by its id, never unattributed.
  const catalog = useLibraryAssetsQuery(true);
  const names = new Map(
    (catalog.data?.items ?? []).map((item) => [item.id, item.name] as const),
  );
  const ordered = [...findings].sort(bySeverity);
  const counts = countBySeverity(findings);

  return (
    <div className="rounded-md border border-border bg-card">
      <p
        {...(announce ? { role: "status", "aria-live": "polite" } : {})}
        className="border-b border-border px-4 py-2 text-xs text-muted-foreground"
      >
        {`${counts.error} error${counts.error === 1 ? "" : "s"}, ` +
          `${counts.warning} warning${counts.warning === 1 ? "" : "s"}, ` +
          `${counts.info} informational` +
          (counts.other > 0
            ? `, ${counts.other} finding${counts.other === 1 ? "" : "s"} with unrecognized severity`
            : "")}
      </p>

      <ul className="list-none">
        {ordered.map((finding, index) => {
          const resolvable = isResolvablePath(finding.path, paths);
          const lockReason = finding.path
            ? lockReasonByPath?.get(finding.path)
            : undefined;

          return (
            <FindingItem
              // `code` repeats across fields and `path` may be null, so neither
              // is a key on its own. Index is stable here because the list is
              // rebuilt whole from one report and never reordered in place.
              key={`${finding.code}:${finding.path ?? ""}:${index}`}
              finding={finding}
              lockReason={lockReason}
              projectId={projectId}
              assetName={
                finding.location?.assetId
                  ? (names.get(finding.location.assetId) ?? null)
                  : null
              }
              {...(resolvable
                ? { onJump: () => focus(finding.path as string) }
                : {})}
            />
          );
        })}
      </ul>
    </div>
  );
}
